import React, { useRef, useMemo, useState, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

const lerp = THREE.MathUtils.lerp;
const CLAMP = 0.26; // ~15 degrees

// ---- Colors ----
const CREAM = "#f1e6d4";
const CREAM_D = "#e7d8c0";
const CARAMEL = "#cf9560";
const WHITE = "#fbf6ef";
const EYE = "#1f1913";
const NOSE = "#e5808f";
const BLUSH = "#f0a3ac";
const BOW = "#e15b7b";
const GOLD = "#e3b23c";
const BED = "#b9d4e8";
const BED_D = "#a3c4dd";
const BED_CUSH = "#cbe0ef";

function fur(color, rough = 0.95) {
  return <meshStandardMaterial color={color} roughness={rough} metalness={0} />;
}

// ---- Expression targets by mood ----
// Aliases let both the app mood names and the rig-spec mood names drive one rig.
export const MOOD_ALIAS = { joy: "excited", play: "excited", sleep: "sleepy", eating: "eat", cuddle: "pet_reaction", levelup: "excited", cheek: "pet_reaction" };

function moodTargets(raw) {
  const mood = MOOD_ALIAS[raw] || raw;
  switch (mood) {
    case "excited": return { eye: "arc", mouth: "big", blush: 0.6, earDroop: 0, tail: 1.8, bounce: true, brow: 0, speed: 1.6, droop: -0.03, whisker: 1 };
    case "pet_reaction": return { eye: "arc", mouth: "big", blush: 0.85, earDroop: 0, tail: 0.9, bounce: true, brow: 0, speed: 1.2, droop: 0, whisker: 1, scrunch: 1 };
    case "eat": return { eye: "open", mouth: "open", blush: 0.4, earDroop: 0.05, tail: 0.5, bounce: false, brow: 0, speed: 1, droop: 0.04 };
    case "hungry": return { eye: "open", mouth: "o", blush: 0.2, earDroop: 0.25, tail: 0.2, bounce: false, brow: 0.2, speed: 1, droop: 0.07 };
    case "sleepy": return { eye: "closed", mouth: "flat", blush: 0.2, earDroop: 0.4, tail: 0, bounce: false, brow: 0, speed: 0.4, droop: 0.1 };
    case "tired": return { eye: "half", mouth: "flat", blush: 0.15, earDroop: 0.55, tail: 0.1, bounce: false, brow: 0.3, speed: 0.5, droop: 0.08 };
    case "sick": return { eye: "half", mouth: "frown", blush: 0.1, earDroop: 0.6, tail: 0, bounce: false, brow: 0.4, speed: 0.5, droop: 0.06 };
    case "sad": return { eye: "open", mouth: "frown", blush: 0.1, earDroop: 0.65, tail: 0, bounce: false, brow: 0.6, speed: 0.7, droop: 0.08, teary: 1 };
    case "dead": return { eye: "x", mouth: "flat", blush: 0, earDroop: 0.8, tail: 0, bounce: false, brow: 0, speed: 0, droop: 0 };
    case "revive": return { eye: "arc", mouth: "big", blush: 0.6, earDroop: 0, tail: 1.2, bounce: true, brow: 0, speed: 1.2, droop: -0.02 };
    default: return { eye: "open", mouth: "smile", blush: 0.35, earDroop: 0.05, tail: 0.4, bounce: false, brow: 0, speed: 1, droop: 0 }; // happy
  }
}

// ---- Eye ----
function Eye({ side, styleRef }) {
  const x = 0.42 * side;
  const ball = useRef();
  const arc = useRef();
  const closed = useRef();
  const xg = useRef();
  useFrame(() => {
    const st = styleRef.current;
    if (!st) return;
    const showBall = st.eye === "open" || st.eye === "half";
    const openAmt = st.eye === "half" ? 0.5 : 1;
    if (ball.current) {
      ball.current.visible = showBall;
      ball.current.scale.y = lerp(ball.current.scale.y, (showBall ? openAmt : 0.1) * st.blink, 0.4);
    }
    if (arc.current) arc.current.visible = st.eye === "arc";
    if (closed.current) closed.current.visible = st.eye === "closed";
    if (xg.current) xg.current.visible = st.eye === "x";
  });
  return (
    <group position={[x, 0.62, 0.92]}>
      <group ref={ball}>
        <mesh scale={[0.82, 1, 0.7]}>
          <sphereGeometry args={[0.2, 24, 24]} />
          <meshStandardMaterial color={EYE} roughness={0.12} metalness={0.1} />
        </mesh>
        <mesh position={[0.07, 0.08, 0.17]}>
          <sphereGeometry args={[0.055, 12, 12]} />
          <meshStandardMaterial color="#ffffff" roughness={0.2} />
        </mesh>
        <mesh position={[-0.05, -0.02, 0.17]}>
          <sphereGeometry args={[0.028, 10, 10]} />
          <meshStandardMaterial color="#ffffff" roughness={0.2} />
        </mesh>
      </group>
      <mesh ref={arc} rotation={[0, 0, 0]} position={[0, 0.02, 0.1]}>
        <torusGeometry args={[0.15, 0.032, 8, 20, Math.PI]} />
        <meshStandardMaterial color={EYE} roughness={0.3} />
      </mesh>
      <mesh ref={closed} rotation={[Math.PI, 0, 0]} position={[0, 0.02, 0.1]}>
        <torusGeometry args={[0.15, 0.032, 8, 20, Math.PI]} />
        <meshStandardMaterial color={EYE} roughness={0.3} />
      </mesh>
      <group ref={xg} position={[0, 0.02, 0.12]}>
        <mesh rotation={[0, 0, Math.PI / 4]}>
          <boxGeometry args={[0.26, 0.045, 0.045]} />
          <meshStandardMaterial color={EYE} />
        </mesh>
        <mesh rotation={[0, 0, -Math.PI / 4]}>
          <boxGeometry args={[0.26, 0.045, 0.045]} />
          <meshStandardMaterial color={EYE} />
        </mesh>
      </group>
    </group>
  );
}

// ---- Mouth ----
function Mouth({ styleRef }) {
  const smile = useRef();
  const open = useRef();
  const tongue = useRef();
  useFrame((s) => {
    const st = styleRef.current;
    if (!st) return;
    const isOpen = st.mouth === "open" || st.mouth === "o";
    if (open.current) {
      open.current.visible = isOpen;
      let target = st.mouth === "o" ? 0.55 : 1;
      if (st.mouth === "open") {
        target = 0.6 + Math.abs(Math.sin(s.clock.elapsedTime * 6)) * 0.6; // chewing
      }
      open.current.scale.set(lerp(open.current.scale.x, 0.9 * target, 0.3), lerp(open.current.scale.y, target, 0.3), 0.6);
    }
    if (tongue.current) tongue.current.visible = isOpen;
    if (smile.current) {
      smile.current.visible = !isOpen;
      const frown = st.mouth === "frown";
      const big = st.mouth === "big";
      smile.current.rotation.z = lerp(smile.current.rotation.z, frown ? Math.PI : 0, 0.3);
      const sc = st.mouth === "flat" ? 0.35 : big ? 1.25 : 1;
      smile.current.scale.set(lerp(smile.current.scale.x, sc, 0.3), lerp(smile.current.scale.y, st.mouth === "flat" ? 0.4 : 1, 0.3), 1);
    }
  });
  return (
    <group position={[0, 0.22, 1.12]}>
      <mesh ref={smile} position={[0, 0, 0]}>
        <torusGeometry args={[0.12, 0.028, 8, 20, Math.PI]} />
        <meshStandardMaterial color="#8a5a52" roughness={0.4} />
      </mesh>
      <mesh ref={open} scale={[0.9, 1, 0.6]}>
        <sphereGeometry args={[0.16, 16, 16]} />
        <meshStandardMaterial color="#7a3b3b" roughness={0.5} />
      </mesh>
      <mesh ref={tongue} position={[0, -0.05, 0.08]} scale={[0.7, 0.5, 0.4]}>
        <sphereGeometry args={[0.12, 12, 12]} />
        <meshStandardMaterial color="#e77e86" roughness={0.5} />
      </mesh>
    </group>
  );
}

function Whiskers({ side }) {
  const rot = side > 0 ? -1 : 1;
  return (
    <group position={[0.42 * side, 0.2, 0.98]}>
      {[0.14, 0, -0.14].map((y, i) => (
        <mesh key={i} position={[0.18 * side, y, 0]} rotation={[0, 0, rot * (0.15 + i * -0.12)]}>
          <cylinderGeometry args={[0.006, 0.006, 0.5, 6]} />
          <meshStandardMaterial color="#9c8071" roughness={0.6} />
        </mesh>
      ))}
    </group>
  );
}

function Ear({ side, droopRef }) {
  const g = useRef();
  useFrame(() => {
    if (g.current && droopRef.current != null) {
      g.current.rotation.z = lerp(g.current.rotation.z, side * (0.25 + droopRef.current * 0.9), 0.15);
    }
  });
  return (
    <group ref={g} position={[0.6 * side, 1.02, 0.15]} rotation={[0, 0, side * 0.25]}>
      <mesh rotation={[0.2, 0, 0]}>
        <coneGeometry args={[0.34, 0.62, 20]} />
        {fur(CREAM)}
      </mesh>
      <mesh position={[0, 0.12, 0.02]} rotation={[0.2, 0, 0]} scale={[0.9, 0.9, 0.9]}>
        <coneGeometry args={[0.3, 0.5, 20]} />
        {fur(CARAMEL)}
      </mesh>
      <mesh position={[0, -0.02, 0.16]} rotation={[0.2, 0, 0]} scale={[0.6, 0.7, 0.5]}>
        <coneGeometry args={[0.22, 0.4, 16]} />
        <meshStandardMaterial color={BLUSH} roughness={0.8} />
      </mesh>
    </group>
  );
}

export function Accessory({ type }) {
  if (!type || type === "bow") return null;
  if (type === "crown") {
    return (
      <group position={[0, 1.12, 0.05]} rotation={[0.08, 0, 0]}>
        <mesh><cylinderGeometry args={[0.5, 0.56, 0.3, 24, 1, true]} /><meshStandardMaterial color={GOLD} roughness={0.35} metalness={0.5} side={THREE.DoubleSide} /></mesh>
        {[0, 1, 2, 3, 4, 5].map((i) => {
          const a = (i / 6) * Math.PI * 2;
          return (
            <mesh key={i} position={[Math.cos(a) * 0.5, 0.28, Math.sin(a) * 0.5]} rotation={[0, -a, 0]}>
              <coneGeometry args={[0.1, 0.26, 4]} /><meshStandardMaterial color={GOLD} roughness={0.35} metalness={0.5} />
            </mesh>
          );
        })}
        <mesh position={[0, 0.05, 0.53]}><sphereGeometry args={[0.07, 12, 12]} /><meshStandardMaterial color="#e63960" roughness={0.3} /></mesh>
      </group>
    );
  }
  if (type === "flower") {
    return (
      <group position={[-0.7, 1.0, 0.5]} rotation={[0, 0, -0.3]}>
        {[0, 1, 2, 3, 4].map((i) => (
          <mesh key={i} rotation={[0, 0, (i / 5) * Math.PI * 2]} position={[Math.cos((i / 5) * Math.PI * 2) * 0.14, Math.sin((i / 5) * Math.PI * 2) * 0.14, 0]}>
            <sphereGeometry args={[0.1, 12, 12]} />
            <meshStandardMaterial color="#f6a5c0" roughness={0.7} />
          </mesh>
        ))}
        <mesh><sphereGeometry args={[0.09, 12, 12]} /><meshStandardMaterial color={GOLD} roughness={0.5} /></mesh>
      </group>
    );
  }
  if (type === "glasses") {
    return (
      <group position={[0, 0.62, 1.02]}>
        {[-0.42, 0.42].map((x) => (
          <mesh key={x} position={[x, 0, 0]}>
            <torusGeometry args={[0.24, 0.03, 10, 24]} />
            <meshStandardMaterial color="#3a2f2a" roughness={0.4} />
          </mesh>
        ))}
        <mesh position={[0, 0, 0]}><boxGeometry args={[0.34, 0.03, 0.03]} /><meshStandardMaterial color="#3a2f2a" /></mesh>
      </group>
    );
  }
  if (type === "hat") {
    return (
      <group position={[0, 1.25, 0.1]} rotation={[0.1, 0, 0.05]}>
        <mesh><cylinderGeometry args={[0.95, 0.95, 0.06, 28]} /><meshStandardMaterial color="#e8d3a1" roughness={0.85} /></mesh>
        <mesh position={[0, 0.22, 0]}><cylinderGeometry args={[0.5, 0.6, 0.44, 28]} /><meshStandardMaterial color="#e8d3a1" roughness={0.85} /></mesh>
        <mesh position={[0, 0.1, 0]}><cylinderGeometry args={[0.62, 0.62, 0.12, 28]} /><meshStandardMaterial color={BOW} roughness={0.7} /></mesh>
      </group>
    );
  }
  if (type === "scarf") {
    return (
      <group position={[0, -1.02, 0.05]}>
        <mesh rotation={[Math.PI / 2, 0, 0]} scale={[1.05, 1, 0.85]}>
          <torusGeometry args={[0.62, 0.2, 14, 30]} />
          <meshStandardMaterial color="#d1495b" roughness={0.9} />
        </mesh>
        {/* hanging ends in front */}
        <mesh position={[0.16, -0.34, 0.55]} rotation={[0.25, 0, 0.12]}>
          <boxGeometry args={[0.26, 0.6, 0.12]} />
          <meshStandardMaterial color="#d1495b" roughness={0.9} />
        </mesh>
        <mesh position={[0.16, -0.64, 0.58]} rotation={[0.25, 0, 0.12]}>
          <boxGeometry args={[0.26, 0.12, 0.12]} />
          <meshStandardMaterial color="#f2e2c4" roughness={0.9} />
        </mesh>
      </group>
    );
  }
  if (type === "balloon") {
    return (
      <group position={[1.15, 1.15, 0.1]}>
        <mesh position={[0, 0.55, 0]} scale={[0.9, 1.1, 0.9]}>
          <sphereGeometry args={[0.4, 20, 20]} />
          <meshStandardMaterial color="#ff5a5f" roughness={0.35} />
        </mesh>
        <mesh position={[0, 0.12, 0]}>
          <coneGeometry args={[0.06, 0.1, 8]} />
          <meshStandardMaterial color="#ff5a5f" roughness={0.35} />
        </mesh>
        <mesh position={[-0.35, -0.55, 0]} rotation={[0, 0, 0.5]}>
          <cylinderGeometry args={[0.008, 0.008, 1.4, 6]} />
          <meshStandardMaterial color="#8a7a70" />
        </mesh>
      </group>
    );
  }
  if (type === "santahat") {
    return (
      <group position={[0, 1.22, 0.05]} rotation={[0.05, 0, 0.12]}>
        <mesh position={[0, 0.02, 0]}><torusGeometry args={[0.62, 0.13, 12, 28]} /><meshStandardMaterial color="#fbfbff" roughness={0.9} /></mesh>
        <mesh position={[0.18, 0.5, 0]} rotation={[0, 0, -0.35]}><coneGeometry args={[0.55, 0.95, 24]} /><meshStandardMaterial color="#d1495b" roughness={0.75} /></mesh>
        <mesh position={[0.42, 0.92, 0]}><sphereGeometry args={[0.14, 16, 16]} /><meshStandardMaterial color="#fbfbff" roughness={0.9} /></mesh>
      </group>
    );
  }
  if (type === "pecimerdeka") {
    return (
      <group position={[0, 1.2, 0.02]}>
        <mesh><cylinderGeometry args={[0.6, 0.66, 0.5, 30]} /><meshStandardMaterial color="#141821" roughness={0.55} /></mesh>
        <mesh position={[0, 0.26, 0]}><cylinderGeometry args={[0.6, 0.6, 0.04, 30]} /><meshStandardMaterial color="#0d0f15" roughness={0.5} /></mesh>
        <mesh position={[0, 0.02, 0.6]}><boxGeometry args={[0.5, 0.16, 0.04]} /><meshStandardMaterial color="#c9363b" roughness={0.6} /></mesh>
      </group>
    );
  }
  if (type === "heartwand") {
    return (
      <group position={[1.1, 1.05, 0.2]} rotation={[0, 0, -0.2]}>
        {[-1, 1].map((s) => (
          <mesh key={s} position={[0.14 * s, 0.16, 0]}><sphereGeometry args={[0.17, 16, 16]} /><meshStandardMaterial color="#ff4d7d" roughness={0.4} /></mesh>
        ))}
        <mesh position={[0, -0.02, 0]} rotation={[0, 0, Math.PI]}><coneGeometry args={[0.26, 0.34, 4]} /><meshStandardMaterial color="#ff4d7d" roughness={0.4} /></mesh>
        <mesh position={[0, -0.55, 0]}><cylinderGeometry args={[0.03, 0.03, 0.9, 8]} /><meshStandardMaterial color={GOLD} roughness={0.35} metalness={0.4} /></mesh>
      </group>
    );
  }
  if (type === "kartiniflower") {
    return (
      <group position={[0.72, 1.02, 0.5]} rotation={[0, 0, 0.2]}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <mesh key={i} position={[Math.cos((i / 6) * Math.PI * 2) * 0.2, Math.sin((i / 6) * Math.PI * 2) * 0.2, 0]}>
            <sphereGeometry args={[0.13, 14, 14]} />
            <meshStandardMaterial color="#d81e5b" roughness={0.6} />
          </mesh>
        ))}
        <mesh><sphereGeometry args={[0.12, 14, 14]} /><meshStandardMaterial color={GOLD} roughness={0.4} metalness={0.3} /></mesh>
      </group>
    );
  }
  if (type === "fireworks") {
    const cols = ["#ffd23f", "#ff5a5f", "#48cae4", "#a5f0c5", "#f78fb3"];
    return (
      <group position={[0.95, 1.55, 0]}>
        <mesh><sphereGeometry args={[0.1, 12, 12]} /><meshStandardMaterial color="#fff3c4" emissive="#ffd23f" emissiveIntensity={0.6} /></mesh>
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i / 12) * Math.PI * 2;
          const r = 0.42;
          return (
            <mesh key={i} position={[Math.cos(a) * r, Math.sin(a) * r, 0]}>
              <sphereGeometry args={[0.055, 8, 8]} />
              <meshStandardMaterial color={cols[i % cols.length]} emissive={cols[i % cols.length]} emissiveIntensity={0.5} />
            </mesh>
          );
        })}
      </group>
    );
  }
  return null;
}

export function Bow() {
  return (
    <group position={[0, 0.2, 0.74]} rotation={[0.25, 0, 0]}>
      {[-1, 1].map((s) => (
        <mesh key={s} position={[0.28 * s, 0, 0]} rotation={[0, 0, s * 0.5]} scale={[1, 0.72, 0.5]}>
          <sphereGeometry args={[0.26, 16, 16]} />
          <meshStandardMaterial color={BOW} roughness={0.55} />
        </mesh>
      ))}
      <mesh><sphereGeometry args={[0.13, 16, 16]} /><meshStandardMaterial color={GOLD} roughness={0.4} metalness={0.3} /></mesh>
    </group>
  );
}

function MochiModel({ mood, accessory, dragRef, actionPulse, onReact, lookRef }) {
  const rig = useRef();
  const bodyGroup = useRef();
  const cheekL = useRef();
  const cheekR = useRef();
  const tail = useRef();
  const styleRef = useRef({ eye: "open", mouth: "smile", blush: 0.35, blink: 1 });
  const droopRef = useRef(0.05);
  const bounceRef = useRef(0);
  const lastPulse = useRef(0);
  const blinkTimer = useRef(2 + Math.random() * 3);

  const target = useMemo(() => moodTargets(mood), [mood]);

  useEffect(() => {
    if (target.bounce) bounceRef.current = 1;
  }, [target]);

  useFrame((s, dt) => {
    const t = s.clock.elapsedTime;
    // expression easing
    styleRef.current.eye = target.eye;
    styleRef.current.mouth = target.mouth;
    styleRef.current.blush = lerp(styleRef.current.blush, target.blush, 0.08);
    droopRef.current = lerp(droopRef.current, target.earDroop, 0.08);

    // blink (only open/half eyes)
    if (target.eye === "open" || target.eye === "half") {
      blinkTimer.current -= dt;
      if (blinkTimer.current < 0.12 && blinkTimer.current > 0) styleRef.current.blink = 0.12;
      else styleRef.current.blink = 1;
      if (blinkTimer.current < 0) blinkTimer.current = 2 + Math.random() * 3.5;
    } else styleRef.current.blink = 1;

    // cheeks blush opacity
    [cheekL, cheekR].forEach((c) => {
      if (c.current) c.current.material.opacity = 0.35 + styleRef.current.blush * 0.55;
    });

    // action pulse -> bounce
    const pulse = (actionPulse || 0) + (dragRef.current?.pulse || 0);
    if (pulse !== lastPulse.current) {
      lastPulse.current = pulse;
      bounceRef.current = 1;
      onReact && onReact();
    }

    // breathing + bounce (speed reacts to mood energy)
    const spd = target.speed == null ? 1 : target.speed;
    const breathe = Math.sin(t * 1.6 * (spd || 0.001)) * 0.02 * (0.4 + spd * 0.6);
    let by = breathe;
    let squash = 1;
    if (bounceRef.current > 0) {
      bounceRef.current = Math.max(0, bounceRef.current - dt * (2.2 + spd));
      const b = bounceRef.current;
      by += Math.sin(b * Math.PI) * 0.16 * (0.7 + spd * 0.4);
      squash = 1 - Math.sin(b * Math.PI) * 0.06;
    }
    if (rig.current) {
      const nmood = MOOD_ALIAS[mood] || mood;
      const sleeping = nmood === "sleepy" || nmood === "dead";
      rig.current.position.y = lerp(rig.current.position.y, by + (sleeping ? -0.12 : 0), 0.2);
      rig.current.scale.y = lerp(rig.current.scale.y, squash, 0.25);
      rig.current.scale.x = lerp(rig.current.scale.x, 1 / squash * 0.5 + 0.5, 0.25);

      // look offset (setLookOffset), clamped ±15°
      const lk = lookRef?.current || { yaw: 0, pitch: 0 };
      const lookY = THREE.MathUtils.clamp((lk.yaw || 0) * Math.PI / 180, -CLAMP, CLAMP);
      const lookX = THREE.MathUtils.clamp((lk.pitch || 0) * Math.PI / 180, -CLAMP, CLAMP);

      // tilt from drag (max 15deg) or look offset; spring back otherwise. Add mood head droop.
      const d = dragRef.current;
      const baseX = (sleeping ? -0.35 : (target.droop || 0)) + lookX;
      const tx = d && d.dragging ? d.targetX : baseX;
      const ty = d && d.dragging ? d.targetY : lookY;
      rig.current.rotation.x = lerp(rig.current.rotation.x, THREE.MathUtils.clamp(tx, -CLAMP - 0.36, CLAMP), 0.12);
      rig.current.rotation.y = lerp(rig.current.rotation.y, THREE.MathUtils.clamp(ty, -CLAMP, CLAMP), 0.12);
      rig.current.rotation.z = lerp(rig.current.rotation.z, nmood === "sleepy" ? 0.18 : 0, 0.1);
    }

    // tail wag
    if (tail.current) {
      tail.current.rotation.z = -0.5 + Math.sin(t * (2 + target.tail * 3)) * 0.12 * (0.3 + target.tail);
    }
  });

  return (
    <group ref={rig} position={[0, 0, 0]}>
      {/* BODY (about 20% smaller, egg shape) */}
      <group ref={bodyGroup} position={[0, -0.95, 0]}>
        <mesh scale={[0.78, 0.92, 0.72]}>
          <sphereGeometry args={[0.85, 32, 32]} />
          {fur(WHITE)}
        </mesh>
        {/* arms */}
        {[-1, 1].map((sd) => (
          <mesh key={sd} position={[0.6 * sd, 0.02, 0.28]} rotation={[0.3, 0, sd * 0.3]} scale={[0.5, 0.72, 0.5]}>
            <sphereGeometry args={[0.34, 20, 20]} />
            {fur(WHITE)}
          </mesh>
        ))}
        {/* feet */}
        {[-1, 1].map((sd) => (
          <group key={sd} position={[0.34 * sd, -0.72, 0.42]}>
            <mesh scale={[0.66, 0.5, 0.85]}>
              <sphereGeometry args={[0.32, 20, 20]} />
              {fur(WHITE)}
            </mesh>
            {[-0.07, 0.07].map((tx) => (
              <mesh key={tx} position={[tx, -0.05, 0.28]}>
                <sphereGeometry args={[0.022, 8, 8]} />
                <meshStandardMaterial color="#c9a98f" roughness={0.7} />
              </mesh>
            ))}
          </group>
        ))}
        {/* tail */}
        <mesh ref={tail} position={[-0.62, -0.1, -0.35]} rotation={[0, 0, -0.5]} scale={[0.4, 0.9, 0.4]}>
          <capsuleGeometry args={[0.16, 0.5, 6, 12]} />
          {fur(CARAMEL)}
        </mesh>
        <Bow />
      </group>

      {/* HEAD (large & cute) */}
      <group position={[0, 0.35, 0]}>
        <mesh scale={[1.08, 1.0, 0.98]}>
          <sphereGeometry args={[1.05, 40, 40]} />
          {fur(CREAM)}
        </mesh>
        {/* caramel patch on top-back */}
        <mesh position={[0.18, 0.5, -0.35]} scale={[1.02, 0.8, 0.92]}>
          <sphereGeometry args={[1.0, 32, 32, 0, Math.PI * 2, 0, Math.PI * 0.62]} />
          {fur(CARAMEL)}
        </mesh>
        {/* white muzzle / chin */}
        <mesh position={[0, 0.16, 0.66]} scale={[0.98, 0.8, 0.72]}>
          <sphereGeometry args={[0.6, 28, 28]} />
          {fur(WHITE)}
        </mesh>
        {/* cheeks */}
        <mesh ref={cheekL} position={[-0.5, 0.28, 0.86]} scale={[0.9, 0.62, 0.55]}>
          <sphereGeometry args={[0.24, 18, 18]} />
          <meshStandardMaterial color={BLUSH} roughness={0.85} transparent opacity={0.6} />
        </mesh>
        <mesh ref={cheekR} position={[0.5, 0.28, 0.86]} scale={[0.9, 0.62, 0.55]}>
          <sphereGeometry args={[0.24, 18, 18]} />
          <meshStandardMaterial color={BLUSH} roughness={0.85} transparent opacity={0.6} />
        </mesh>
        {/* nose (higher on face) */}
        <mesh position={[0, 0.4, 1.14]} scale={[1, 0.8, 0.8]}>
          <sphereGeometry args={[0.11, 18, 18]} />
          <meshStandardMaterial color={NOSE} roughness={0.4} />
        </mesh>

        <Eye side={-1} styleRef={styleRef} />
        <Eye side={1} styleRef={styleRef} />
        <Mouth styleRef={styleRef} />
        <Whiskers side={-1} />
        <Whiskers side={1} />
        <Ear side={-1} droopRef={droopRef} />
        <Ear side={1} droopRef={droopRef} />
        <Accessory type={accessory} />
      </group>
    </group>
  );
}

function PetBed({ bedColor }) {
  const main = bedColor || BED;
  return (
    <group position={[0, -1.95, 0]}>
      {/* base mattress */}
      <mesh position={[0, 0, 0]}>
        <cylinderGeometry args={[1.9, 1.9, 0.5, 40]} />
        <meshStandardMaterial color={main} roughness={0.95} />
      </mesh>
      {/* soft cushion top */}
      <mesh position={[0, 0.28, 0]} scale={[1, 0.5, 1]}>
        <sphereGeometry args={[1.55, 32, 32, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color={BED_CUSH} roughness={0.98} />
      </mesh>
      {/* bolster ring at back/sides */}
      {[[-1.35, 0.2, -0.4, 0.6], [1.35, 0.2, -0.4, -0.6], [0, 0.2, -1.4, 0]].map((p, i) => (
        <mesh key={i} position={[p[0], p[1] + 0.1, p[2]]} rotation={[0, p[3], 0]}>
          <torusGeometry args={[0.62, 0.32, 16, 28, Math.PI]} />
          <meshStandardMaterial color={main} roughness={0.98} />
        </mesh>
      ))}
      <mesh position={[0, 0.34, -1.15]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[1.35, 0.34, 16, 40, Math.PI]} />
        <meshStandardMaterial color={main} roughness={0.98} />
      </mesh>
    </group>
  );
}

function Particles({ mood, color }) {
  const g = useRef();
  const kind = ["joy", "play", "cuddle", "revive", "happy"].includes(mood);
  useFrame((s) => {
    if (!g.current) return;
    g.current.visible = kind;
    g.current.children.forEach((c, i) => {
      const t = s.clock.elapsedTime + i * 1.3;
      c.position.y = 1.4 + ((t * 0.4) % 1.6);
      c.position.x = Math.sin(t * 0.8 + i) * 1.4;
      const m = c.material;
      m.opacity = Math.max(0, 0.8 - ((t * 0.4) % 1.6) / 2);
    });
  });
  return (
    <group ref={g}>
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={i} position={[0, 1.4, 0.4]}>
          <sphereGeometry args={[0.06, 8, 8]} />
          <meshStandardMaterial color={i % 2 ? BOW : (color || "#f3c9a0")} transparent opacity={0.7} roughness={0.6} />
        </mesh>
      ))}
    </group>
  );
}

export default function MochiCanvas({ mood = "happy", accessory = "bow", actionPulse = 0, onReact, mini = false, theme, look }) {
  const dragRef = useRef({ dragging: false, targetX: 0, targetY: 0, startX: 0, startY: 0, moved: false, pulse: 0 });
  const lookRef = useRef({ yaw: 0, pitch: 0 });
  lookRef.current = look || lookRef.current;
  const wrapRef = useRef();

  // drag-near head tilt: curious look toward pointer even without pressing
  const hover = (e) => {
    const d = dragRef.current;
    if (d.dragging) return;
    const r = wrapRef.current?.getBoundingClientRect?.();
    if (!r) return;
    const nx = ((e.clientX - r.left) / r.width - 0.5) * 2;
    const ny = ((e.clientY - r.top) / r.height - 0.5) * 2;
    lookRef.current = { yaw: THREE.MathUtils.clamp(nx * 12, -14, 14), pitch: THREE.MathUtils.clamp(ny * 8, -12, 12) };
  };

  const down = (e) => {
    const d = dragRef.current;
    d.dragging = true; d.moved = false;
    d.startX = e.clientX; d.startY = e.clientY;
    wrapRef.current?.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    const d = dragRef.current;
    if (!d.dragging) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (Math.abs(dx) + Math.abs(dy) > 6) d.moved = true;
    d.targetY = THREE.MathUtils.clamp(dx * 0.012, -CLAMP, CLAMP);
    d.targetX = THREE.MathUtils.clamp(dy * 0.010, -CLAMP, CLAMP);
  };
  const up = (e) => {
    const d = dragRef.current;
    if (d.dragging && !d.moved) d.pulse += 1; // tap = react
    d.dragging = false;
    try { wrapRef.current?.releasePointerCapture?.(e.pointerId); } catch {}
  };

  return (
    <div
      ref={wrapRef}
      onPointerDown={down}
      onPointerMove={(e) => { move(e); hover(e); }}
      onPointerUp={up}
      onPointerLeave={() => { dragRef.current.dragging = false; lookRef.current = { yaw: 0, pitch: 0 }; }}
      style={{ width: "100%", height: "100%", cursor: "grab", touchAction: "pan-y" }}
      data-testid="mochi-canvas"
    >
      <Canvas
        camera={{ position: [0, 0.25, mini ? 7.6 : 7.0], fov: 38 }}
        dpr={[1, 2]}
        gl={{ antialias: true, alpha: true }}
        style={{ background: "transparent" }}
      >
        <ambientLight intensity={0.85} />
        <directionalLight position={[3, 6, 5]} intensity={1.1} castShadow />
        <directionalLight position={[-4, 2, 2]} intensity={0.4} color="#ffd9c9" />
        <hemisphereLight args={["#fff5ec", "#d8c6b0", 0.5]} />
        <group position={[0, 0.15, 0]} scale={0.84}>
          <MochiModel mood={mood} accessory={accessory} dragRef={dragRef} actionPulse={actionPulse} onReact={onReact} lookRef={lookRef} />
          <PetBed bedColor={theme?.bed} />
          <Particles mood={mood} color={theme?.particle} />
        </group>
      </Canvas>
    </div>
  );
}
